/**
 * Shared client-side navigation primitives — the single source of truth for
 * History API manipulation across every app (Recruiter, Scoring, Candidate, Admin).
 *
 * Extracted from the per-app inline `navigateTo` helpers so history handling
 * (and listener notification) is consistent everywhere.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
/** App-relative location including query and hash: `pathname + search + hash`. */
export function getRelativeLocation() {
    return window.location.pathname + window.location.search + window.location.hash;
}
/** Current pathname only (no query string or hash). */
export function getCurrentPath() {
    return window.location.pathname;
}
/**
 * Go to a path via the History API and notify listeners. Dispatches a `popstate` event so path-observing
 * routers/components update.
 */
export function writeHistory(path, write) {
    if (write.replace) {
        window.history.replaceState(write.state, '', path);
    }
    else {
        window.history.pushState(write.state, '', path);
    }
    window.dispatchEvent(new PopStateEvent('popstate', { state: write.state }));
}
/** Navigate to an app-relative path via the History API and notify listeners. */
export function navigateTo(path, options = {}) {
    writeHistory(path, { replace: options.replace === true, state: null });
}
/**
 * Remove the given query params from the current URL via `replaceState`, keeping the
 * pathname and hash intact and firing no navigation event. For cleaning up one-shot
 * params (e.g. post-checkout flags) once they've been consumed.
 */
export function stripQueryParams(keys) {
    const params = new URLSearchParams(window.location.search);
    let changed = false;
    for (const key of keys) {
        if (params.has(key)) {
            params.delete(key);
            changed = true;
        }
    }
    if (!changed)
        return;
    const search = params.toString();
    window.history.replaceState(null, '', `${window.location.pathname}${search ? `?${search}` : ''}${window.location.hash}`);
}
/** The full URL path of a path inside the app: `/recruiter` + `/jobs/1`. */
export function appHref(basePath, path) {
    return `${basePath}${path}`;
}
function readAppLocation(basePath, defaultPath) {
    const { pathname } = window.location;
    const inside = pathname.startsWith(`${basePath}/`) ? pathname.slice(basePath.length) : '/';
    return { path: inside === '/' ? defaultPath : inside, state: window.history.state };
}
/**
 * React hook: the history of an app served under `basePath`, reactive to Back/Forward and to every
 * `writeHistory`/`navigateTo`. The app's root (and any path outside it) reads as `defaultPath`.
 */
export function useAppHistory(basePath, defaultPath) {
    const [location, setLocation] = useState(() => readAppLocation(basePath, defaultPath));
    useEffect(() => {
        const onChange = () => setLocation(readAppLocation(basePath, defaultPath));
        window.addEventListener('popstate', onChange);
        return () => window.removeEventListener('popstate', onChange);
    }, [basePath, defaultPath]);
    const go = useCallback((path, write) => writeHistory(appHref(basePath, path), write), [basePath]);
    const back = useCallback(() => window.history.back(), []);
    return useMemo(() => ({ location, go, back }), [location, go, back]);
}
/** React hook: the current pathname, reactive to browser and programmatic navigation. */
export function useCurrentPath() {
    const [path, setPath] = useState(() => window.location.pathname);
    useEffect(() => {
        const onChange = () => setPath(window.location.pathname);
        window.addEventListener('popstate', onChange);
        window.addEventListener('hashchange', onChange);
        return () => {
            window.removeEventListener('popstate', onChange);
            window.removeEventListener('hashchange', onChange);
        };
    }, []);
    return path;
}
